import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { MenubarModule } from 'primeng/menubar';
import { StoreService } from '../../services/store.service';
import { ButtonModule } from 'primeng/button';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginClientService } from '../../services/login-client.service';
import { Subject, take, takeUntil } from 'rxjs';
import { SecurityService } from '../../services/security.service';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, MenuModule, MenubarModule, ButtonModule, TranslocoModule],
  providers:[provideTranslocoScope()],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css',
})
export class NavbarComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();

  username?: string;

  items: MenuItem[] = [];

  itemsLogin: MenuItem[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private storeService: StoreService,
    private loginClientService: LoginClientService,
    private securityService: SecurityService,
  ) {
    effect(() => {
      const selectedContrat = this.storeService.selectedContract();
      this.items = this.initMenuItems();
    });
  }

  ngOnInit() {
    if (!this.storeService.utilisateur) {
      this.loginClientService
        .getUser()
        .pipe(take(1))
        .subscribe((loginResponse) => {
          if (!!loginResponse.utilisateur.id) {
            this.username = loginResponse.utilisateur.username;
            this.storeService.setUtilisateur(loginResponse.utilisateur);
            this.storeService.setShowInfosContratSansInfosClient(loginResponse.utilisateur.showInfosContratSansInfosClient);
          }
        });
    } else {
      this.username = this.storeService.utilisateur?.username;
    }

    this.items = this.initMenuItems();

    this.itemsLogin = [
      {
        label: 'button.layout.updatePassword',
        route: '/password-update',
        icon: 'pi pi-user-edit',
      },
      {
        label: 'button.layout.logout',
        icon: 'pi pi-sign-out',
        command: (event) => {
          this.logout();
        },
      },
    ];
  }

  private initMenuItems(): MenuItem[] {
    return [
      {
        label: 'menu.level1.grille',
        icon: 'pi pi-table',
        display: this.securityService.canDisplayGrid(),
        items: [
          {
            label: 'menu.level2.grille.producteursdechets',
            route: '/producers-wastes',
            display: this.securityService.canDisplayProducersWastes(),
          },
          {
            label: 'menu.level2.grille.fournisseurs',
            route: '/suppliers',
            display: this.securityService.canDisplaySuppliers(),
          },
          {
            label: 'menu.level2.grille.tarifs',
            route: '/price-list',
            display: this.securityService.canDisplayPriceList(),
          },
          {
            label: 'menu.level2.grille.mass.price',
            route: '/mass-price',
            display: this.securityService.canDisplayTransportMassPrice(),
          },
        ],
      },
      {
        label: 'menu.level1.demandes',
        icon: 'pi pi-calendar-plus',
        display: this.securityService.canDisplayDemands(),
        items: [
          {
            label: 'menu.level2.demandes.liste',
            route: '/demand-prestations',
            display: this.securityService.canDisplayDemandPrestations(),
          },
        ],
      },
      {
        label: 'menu.level1.prestations',
        icon: 'pi pi-tags',
        display: this.securityService.canDisplayServices(),
        items: [
          {
            label: 'menu.level2.prestations.prestations',
            route: '/prestations-completed',
            display: this.securityService.canDisplayPrestationsCompleted(),
          },
          {
            label: 'menu.level2.prestations.integrer',
            route: '/integrate-services',
            display: this.securityService.canDisplayIntegratePrestationEffectuees(),
          },
          {
            label: "menu.level2.prestations.commandes",
            route: '/purchase-orders',
            display: this.securityService.canDisplayPurchaseOrders(),
          },
        ],
      },
      {
        label: 'menu.level1.facturation',
        icon: 'pi pi-euro',
        display: this.securityService.canDisplayBilling(),
        items: [
          {
            label: 'menu.level2.facturation.preparerfacturation',
            route: '/billing-preparation',
            display: this.securityService.canDisplayBillingPreparation(),
          },
          {
            label: 'menu.level2.facturation.controlerfacturation',
            route: '/customer-control',
            display: this.securityService.canDisplayCustomerControl(),
          },
          {
            label: 'menu.level2.facturation.relevetechnique',
            route: '/technical-reports',
            display: this.securityService.canDisplayTechnicalReports(),
          },
          {
            label: 'menu.level2.facturation.reporting',
            route: '/reporting',
            display: this.securityService.canDisplayReporting(),
          },
        ],
      },
      {
        label: 'menu.level1.parametres',
        icon: 'pi pi-cog',
        display: this.securityService.canDisplayParameters(),
        items: [
          {
            label: 'menu.level2.administration.contrat',
            route: '/contracts',
            display: this.securityService.canDisplayContracts(),
          },
          {
            label: 'menu.level2.administration.users',
            route: '/users',
            display: this.securityService.canDisplayUsers(),
          },
          {
            label: 'menu.level2.administration.tgap',
            route: '/tgap',
            display: this.securityService.canDisplayReferentials(),
          },
          {
            label: 'menu.level2.administration.referentiel',
            route: '/referentials',
            display: this.securityService.canDisplayReferentials(),
          },
          {
            label: 'menu.level2.administration.monitoring',
            route: '/monitoring',
            display: this.securityService.canDisplayMonitoring(),
          },
          {
            label: 'menu.level2.administration.configuration',
            route: '/configuration',
            display: this.securityService.canDisplayConfiguration(),
          },
        ],
      },
    ];
  }

  logout() {
    this.loginClientService
      .logout()
      .pipe(takeUntil(this.destroy$))
      .subscribe((_) => {
        this.storeService.clear();
        this.router.navigate(['/login']);
        localStorage.removeItem('isLogged');
        localStorage.removeItem('isContractSelected');
        localStorage.removeItem('selectedContract');
        localStorage.removeItem('selectedLanguage');
        localStorage.removeItem('_csrf');
        localStorage.removeItem('contracts');
        localStorage.removeItem('authorities');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
